// ---------------------------------------------------------------------------
// Billing guides - the invoice, from raised to paid.
//
// The money setup guides in setup.js decided what an invoice looks like and who
// issues it. This one is about the document itself: where it comes from, what
// the statuses mean, and what you do with it once it exists.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';
import { COACH_EVENT } from '../events';

const INVOICES = PATH_ADMIN.orderManagement.invoiceList;

export const BILLING_FIRST_INVOICE = {
  id: 'billing-first-invoice',
  title: 'Raise your first invoice',
  summary:
    'Where invoices come from, what each status means, and how to send one and '
    + 'record the money against it.',
  category: 'setup',
  level: 'advanced',
  permission: ['billing.view', 'billing.create'],
  featureSlug: null,
  estMinutes: 6,
  readinessKey: 'first_invoice',
  keywords: [
    'invoice', 'bill', 'raise invoice', 'send invoice', 'unpaid', 'overdue',
    'proforma', 'mark paid', 'payment',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'Invoices are produced, not typed',
      body:
        'Almost every invoice here is the by-product of something else. Sell a '
        + 'membership and an invoice is raised with it. Renew one and the next '
        + 'period is invoiced. Book a meeting room that is not covered by credits '
        + 'and that becomes a line too. You rarely sit down to write an invoice. '
        + 'You do the thing, and the bill follows.',
    },
    {
      id: 'statuses',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'Read the list before you touch it',
      body:
        'The status on each row is the whole story. Unpaid means it has been '
        + 'raised and nothing has come in. Partially paid means some has, which '
        + 'happens when a customer pays a deposit or deducts tax at source. '
        + 'Overdue is the same as unpaid, just past its due date, and that filter '
        + 'is the one worth checking every Monday. A proforma is not a tax invoice '
        + 'yet, it is a quote for payment, and it only becomes a real numbered '
        + 'invoice when you convert it.',
    },
    {
      id: 'create',
      kind: 'act',
      route: INVOICES,
      target: ANCHOR.invoicesCreate,
      placement: 'bottom',
      title: 'Raise one now',
      body:
        'This is the same New Order button as the orders page, which is the point: '
        + 'you invoice by selling something. Pick Monthly Plan for a membership. '
        + 'If you need a one-off bill with no plan behind it, like a fit-out '
        + 'charge, use Manual Entry at the bottom of the dialog instead.',
      waitingLabel: 'Click New Order.',
      advanceOn: [
        { type: 'click', target: ANCHOR.invoicesCreate },
        { type: 'event', name: COACH_EVENT.invoiceCreated },
      ],
      doItForMe: { type: 'click', target: ANCHOR.invoicesCreate },
      allowSkip: true,
    },
    {
      id: 'wait-for-invoice',
      kind: 'await',
      target: CENTER,
      placement: 'center',
      title: 'Finish the order and the invoice writes itself',
      body:
        'The number comes from your series, the letterhead from the billing '
        + 'centre, and the tax from that entity\'s configuration. Nothing here is '
        + 'typed by hand, which is exactly why it was worth setting all three up '
        + 'before this moment.',
      waitingLabel: 'Waiting for the invoice to be raised.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.invoiceCreated }],
      allowSkip: true,
    },
    {
      id: 'send-it',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'Send it, and let them pay it themselves',
      body:
        'The row menu sends the invoice by email or WhatsApp with the PDF '
        + 'attached. If you connected a payment gateway, that message carries a '
        + 'pay link and a successful payment marks the invoice paid on its own, '
        + 'with no one touching this page. You can also select several rows and '
        + 'send them in one go, which is how most operators handle the first of '
        + 'the month.',
    },
    {
      id: 'record-payment',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'Money that arrives another way',
      body:
        'For a bank transfer or cash, open the invoice and record the payment '
        + 'against it. Enter what actually landed rather than the invoice total, '
        + 'because part payments and tax deducted at source are normal and the '
        + 'system tracks the balance properly. Every receipt is kept, so the '
        + 'payment history on an invoice tells you who paid what and when.',
    },
    {
      id: 'done',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'That is the full loop',
      body:
        'Space, plan, member, subscription, invoice, payment. Everything else in '
        + 'DeskOS hangs off that spine. If you got an invoice wrong, do not delete '
        + 'it, raise a credit note against it, because a numbering sequence with '
        + 'holes in it is the one thing an auditor will always find.',
    },
  ],
};

export default [BILLING_FIRST_INVOICE];
